// SHRINKING A SCREENSHOT BEFORE IT LEAVES THE BROWSER.
//
// WHY THIS EXISTS AT ALL, and it is not about bandwidth. A 6K monitor screenshot is ~8 MB of PNG
// and about 3,000 tokens of image once a model looks at it. Downscaled to 1600 px on the long
// side it is a few hundred kilobytes and roughly 1,200–1,600 tokens — and, crucially, it still
// reads: the text in a Calendly confirmation or a lecture slide is legible at that size, which is
// the only thing the shrink must not break.
//
// 1600 AND 0.85 ARE THE TWO NUMBERS. Anthropic's own guidance is that an image beyond ~1568 px on
// its longest edge is resized server-side anyway, so sending more is paying to transmit pixels
// that get thrown away. JPEG at 0.85 is where screenshot text stops visibly degrading.
//
// AN IMAGE SMALLER THAN THE CEILING IS SENT AS IT IS, untouched — re-encoding a 900 px PNG as
// JPEG would only add artefacts to something that already fit.

/** The longest edge, in pixels, that anything leaves this browser at. */
export const MAX_IMAGE_EDGE = 1600

/** JPEG quality for a re-encoded image. Below this, small text in a screenshot starts to smear. */
export const IMAGE_QUALITY = 0.85

/** The three types the chat API stores. Anything else is refused before an upload is attempted. */
export const ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const

export function isAcceptedImage(file: Blob): boolean {
  return (ACCEPTED_IMAGE_TYPES as readonly string[]).includes(file.type)
}

/**
 * The same picture, no bigger than `MAX_IMAGE_EDGE` on its longest side.
 *
 * Returns the ORIGINAL blob when it already fits, or when anything in the canvas path fails —
 * a browser that cannot decode the image is a browser that should still be able to send it, and
 * the Worker's own size ceiling is the backstop either way. Failing the send over a resize that
 * did not work would be losing the owner's screenshot to an optimisation.
 */
export async function downscaleImage(file: Blob): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file)
    const longest = Math.max(bitmap.width, bitmap.height)
    if (longest <= MAX_IMAGE_EDGE) {
      bitmap.close()
      return file
    }

    const scale = MAX_IMAGE_EDGE / longest
    const width = Math.round(bitmap.width * scale)
    const height = Math.round(bitmap.height * scale)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      bitmap.close()
      return file
    }
    ctx.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()

    const shrunk = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', IMAGE_QUALITY),
    )
    // A re-encode that came out BIGGER is thrown away. It happens with flat-colour screenshots,
    // where PNG beats JPEG outright, and sending the larger of two identical pictures would be
    // this function doing the opposite of its job.
    return shrunk && shrunk.size < file.size ? shrunk : file
  } catch {
    return file
  }
}
